import { useEffect, useState } from 'react';
import { money } from '../form';
import { useI18n } from '../i18n';
import type { Project } from './catalog';

/** Filters of the invoice lists, kept in the address (so a reload, the back button or a shared link keep them). */
export type HubTab = 'all' | 'out' | 'in' | 'inbox' | 'vat';
export type HubView = 'unpaid' | 'paid' | 'draft' | 'credit' | 'proforma' | 'all';
export interface ListSearch { q?: string; status?: string; project?: number; from?: string; to?: string; tab?: HubTab; view?: HubView; year?: number }
const TABS: HubTab[] = ['all', 'out', 'in', 'inbox', 'vat'];
const VIEWS: HubView[] = ['unpaid', 'paid', 'draft', 'credit', 'proforma', 'all'];

const DAY = /^\d{4}-\d{2}-\d{2}$/;

export function validateListSearch(s: Record<string, unknown>): ListSearch {
  const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim().slice(0, 100) : undefined);
  const day = (v: unknown) => (typeof v === 'string' && DAY.test(v) ? v : undefined);
  const out: ListSearch = {};
  const q = text(s.q); if (q) out.q = q;
  const status = text(s.status); if (status && /^[a-z]+$/.test(status)) out.status = status;
  if (Number(s.project) > 0) out.project = Number(s.project);
  const from = day(s.from); if (from) out.from = from;
  const to = day(s.to); if (to) out.to = to;
  if (TABS.includes(s.tab as HubTab)) out.tab = s.tab as HubTab;
  if (VIEWS.includes(s.view as HubView)) out.view = s.view as HubView;
  if (Number(s.year) >= 2000 && Number(s.year) <= 2100) out.year = Number(s.year);
  return out;
}

/** Query string for GET /api/v1/invoices. */
export function listQuery(direction: 'outgoing' | 'incoming' | 'all', s: ListSearch): string {
  return new URLSearchParams({
    direction, ...(s.q ? { q: s.q } : {}), ...(s.status ? { status: s.status } : {}), ...(s.project ? { projectId: String(s.project) } : {}),
    ...(s.from ? { from: s.from } : {}), ...(s.to ? { to: s.to } : {}), ...(s.view ? { view: s.view } : {}),
  }).toString();
}

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function periods(): Record<string, [string, string]> {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  return {
    thisMonth: [iso(new Date(y, m, 1)), iso(new Date(y, m + 1, 0))],
    lastMonth: [iso(new Date(y, m - 1, 1)), iso(new Date(y, m, 0))],
    thisQuarter: [iso(new Date(y, m - (m % 3), 1)), iso(new Date(y, m - (m % 3) + 3, 0))],
    thisYear: [`${y}-01-01`, `${y}-12-31`],
    lastYear: [`${y - 1}-01-01`, `${y - 1}-12-31`],
  };
}
const PERIOD_LABELS: Record<string, string> = { thisMonth: 'Šis mėnuo', lastMonth: 'Praėjęs mėnuo', thisQuarter: 'Šis ketvirtis', thisYear: 'Šie metai', lastYear: 'Praėję metai' };

export function ListFilters({ search, onSearch, statuses, projects, placeholder }: {
  search: ListSearch; onSearch: (s: ListSearch) => void; statuses: [string, string][]; projects: Project[]; placeholder: string;
}) {
  const { t } = useI18n();
  // Typing waits a moment before it changes the address (and the query).
  const [q, setQ] = useState(search.q ?? '');
  useEffect(() => { setQ(search.q ?? ''); }, [search.q]);
  useEffect(() => {
    if (q.trim() === (search.q ?? '')) return;
    const timer = window.setTimeout(() => onSearch(clean({ ...search, q: q.trim() })), 350);
    return () => window.clearTimeout(timer);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (patch: Partial<ListSearch>) => onSearch(clean({ ...search, ...patch }));
  const all = periods();
  const period = Object.entries(all).find(([, [from, to]]) => from === search.from && to === search.to)?.[0] ?? (search.from || search.to ? 'custom' : '');
  const active = Boolean(search.q || search.status || search.project || search.from || search.to);
  const keep: ListSearch = { ...(search.tab ? { tab: search.tab } : {}), ...(search.view ? { view: search.view } : {}) };
  return (
    <div className="list-filters list-filters--wide">
      <input type="search" placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('Paieška')} />
      {statuses.length ? (
        <select value={search.status ?? ''} onChange={(e) => set({ status: e.target.value })} aria-label={t('Būsena')}>
          <option value="">{t('Visos būsenos')}</option>
          {statuses.map(([value, label]) => <option key={value} value={value}>{t(label)}</option>)}
        </select>
      ) : null}
      {projects.length ? (
        <select value={search.project ?? ''} onChange={(e) => set({ project: Number(e.target.value) || 0 })} aria-label={t('Projektas')}>
          <option value="">{t('Visi projektai')}</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      ) : null}
      <select className="list-period" value={period} onChange={(e) => { const range = all[e.target.value]; set(range ? { from: range[0], to: range[1] } : { from: '', to: '' }); }} aria-label={t('Laikotarpis')}>
        <option value="">{t('Visas laikotarpis')}</option>
        {Object.keys(all).map((key) => <option key={key} value={key}>{t(PERIOD_LABELS[key]!)}</option>)}
        {period === 'custom' ? <option value="custom">{t('Pasirinktas')}</option> : null}
      </select>
      <input type="date" value={search.from ?? ''} onChange={(e) => set({ from: e.target.value })} aria-label={t('Nuo')} title={t('Nuo')} />
      <input type="date" value={search.to ?? ''} onChange={(e) => set({ to: e.target.value })} aria-label={t('Iki')} title={t('Iki')} />
      {active ? <button type="button" className="text-link" onClick={() => { setQ(''); onSearch(keep); }}>{t('Išvalyti')}</button> : null}
    </div>
  );
}

/** Drops empty values so the address stays short. */
function clean(s: ListSearch): ListSearch {
  return Object.fromEntries(Object.entries(s).filter(([, v]) => v !== '' && v !== 0 && v !== undefined)) as ListSearch;
}

/** "12 sąsk. · 3 450,00 €" under a list, and CSV files of exactly what the list shows (for the accountant). */
export function ListSummary({ rows, exportQuery }: { rows: { total: number }[]; exportQuery?: string }) {
  const { t } = useI18n();
  if (!rows.length) return null;
  return <p className="list-summary">{exportQuery ? <span className="list-export">{t('Eksportuoti CSV')}: <a href={`api/v1/export/invoices.csv?${exportQuery}`} download>{t('sąskaitos')}</a> · <a href={`api/v1/export/lines.csv?${exportQuery}`} download>{t('eilutės')}</a></span> : null}{rows.length} {t('sąsk.')} · <strong>{money(rows.reduce((sum, r) => sum + r.total, 0))}</strong>{rows.length >= 500 ? ` · ${t('rodomos naujausios 500')}` : ''}</p>;
}
